import {useState, useRef, useEffect} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { HelpCircle } from 'lucide-react';
import FormulaireContact from "@/pages/contact/contactez-nous/formulaire-contact";
import ContractPickerModal from '@/components/shared/contract-picker-modal';
import { pushEvent } from '@/lib/analytics';
import type { Contract } from '@/types/api/contract';
import reclamation from "@/assets/images/contact/reclamation.png";
import paiements from "@/assets/images/contact/paiements.png";
import resiliation from "@/assets/images/contact/resiliation.png";
import vehicule_supp from "@/assets/images/contact/vehicule_supp.png";
import dec_sinistre from "@/assets/images/contact/dec_sinistre.png";
import contract_sign from "@/assets/images/contact/contract_sign.png";
import chat from "@/assets/images/contact/chat.png";
import espace_assure from "@/assets/images/contact/espace_assure.png";
import change_vehicule from "@/assets/images/contact/change_vehicule.png";
import send_docs from "@/assets/images/contact/send_docs.png";
import phoneCall from "@/assets/images/contact/phone-call.png";
import update_contract from "@/assets/images/contact/update_contract.png";
import contract from "@/assets/images/contact/contract.png";
import sinistre_encours from "@/assets/images/contact/sinistre_encours.png";
import conversation from "@/assets/images/contact/conversation.png";
import sinistre from "@/assets/images/contact/sinistre.png";
import info_contract from "@/assets/images/contact/info_contract.png";
import constatYes from "@/assets/images/contact/constat_yes.png"
import constatNo from "@/assets/images/contact/constat_no.png"
import useBrandName from '@/hooks/use-brand-name'

/** Choix proposé à une étape du parcours. */
export interface ContactOption {
    value: string;
    label: string;
    icon: string;
    redirect?: string;
    html?: string;
    link?: string;
    onClickAction?: ContractAction;
}

/** Action sur un contrat déclenchée par une option (`avenant`). */
type ContractAction = string;

interface StepDependency {
    field: string;
    values: string[];
}

interface ContactStep {
    id: string;
    type: 'radio' | 'group';
    label?: string;
    info?: string;
    depends_on?: StepDependency[];
    custom_visibility?: 'or';
    options?: ContactOption[] | Record<string, ContactOption[]>;
}

/** Réponses du parcours : identifiant d'étape → valeur de l'option choisie. */
export type ContactFormState = Record<string, string>;

export type GetOptionLabel = (fieldId: string, optionValue: string | undefined, state?: ContactFormState) => string;

declare global {
    interface Window {
        handleMultipleContracts?: (action: ContractAction) => void;
    }
}

export interface ContactezNousProps {
    contracts: Contract[];
    subscribeLinks: Record<string, string>;
    /** Tous les contrats sont en apporteur 43397. */
    hasApporteur43397: boolean;
    /** Au moins un contrat en apporteur 43397. */
    hasContractApporteur43397: boolean;
}

const renderOptionInner = (opt: ContactOption) => (
    <>
        <img src={opt.icon} alt="" aria-hidden="true" className="w-[50px] mb-2" />
        <span className="text-primary" dangerouslySetInnerHTML={{ __html: opt.label }} />
        {opt.html && (
            <div
                className="mt-1 text-xs [&_a]:text-primary-foreground [&_a]:no-underline [&_a]:font-semibold"
                dangerouslySetInnerHTML={{ __html: opt.html }}
            />
        )}
    </>
);

const ContactezNous = ({contracts, subscribeLinks, hasApporteur43397, hasContractApporteur43397}: ContactezNousProps) => {
    const brandName = useBrandName();
    const faqOption = (redirect: string): ContactOption[] => [
        {
            value: 'consult_faq',
            label: 'Consulter la Foire Aux Questions',
            redirect: redirect,
            icon: conversation,
        },
    ];
    const envoyerMessageOption: ContactOption[] = [{
        "value": "envoyer_message",
        "label": "Envoyer un message",
        "icon": chat
    }];
    const [formState, setFormState] = useState<ContactFormState>({});
    const [searchParams] = useSearchParams();
    const deepLinkApplied = useRef(false);
    const refs = useRef<Record<string, HTMLDivElement | null>>({});
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerAction, setPickerAction] = useState<ContractAction | null>(null);

    useEffect(() => {
        const handleBeforeUnload = () => {
            if (Object.keys(formState).length > 0) {
                const trackingInfo = getFormTrackingInfo();

                pushEvent('formAbandon', {
                    niveau: trackingInfo.niveau,
                    type_client: trackingInfo.type_client,
                    motif_contact: trackingInfo.motif_contact
                });
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [formState]);

    const getStepLabel = (fieldId: string) => {
        const step = formStructure.etapes.find(s => s.id === fieldId);
        if(fieldId === 'motif_contact'){

            return step!.label?.replace(/<[^>]*>/g, '');
        }
        return step ? step.label?.replace(/<[^>]*>/g, '') : '';
    };

    const getOptionLabel: GetOptionLabel = (fieldId, optionValue, state = formState) => {
        const step = formStructure.etapes.find(s => s.id === fieldId);
        if (!step) return '';

        let options: ContactOption[] = [];
        if (Array.isArray(step.options)) {
            options = step.options;
        } else if (typeof step.options === 'object') {
            const possibleKeys = Object.keys(step.options);
            for (const dep of (step.depends_on || [])) {
                const userVal = state[dep.field];
                if (userVal && possibleKeys.includes(userVal)) {
                    options = step.options[userVal];
                    break;
                }
            }
        }

        const option = options.find(opt => opt.value === optionValue);
        return option ? option.label : '';
    };

    const getParentLabels = (currentStepId: string, state: ContactFormState = formState) => {
        const parentLabels: string[] = [];
        const currentStepIndex = formStructure.etapes.findIndex(s => s.id === currentStepId);

        for (let i = 0; i < currentStepIndex; i++) {
            const step = formStructure.etapes[i];
            const selectedValue = state[step.id];
            if (selectedValue) {
                const label = getOptionLabel(step.id, selectedValue, state);
                if (label) {
                    parentLabels.push(label);
                }
            }
        }

        return parentLabels;
    };

    const getFormTrackingInfo = () => {
        return {
            niveau: Object.keys(formState).length,
            motif_contact: formState.motif_contact || null,
            type_client: 'Déjà client'
        };
    };

    const formStructure: { etapes: ContactStep[] } = {
        etapes: [
            {
                label: 'Quel est le sujet de <span>votre demande ?</span>',
                info: 'Choisissez la catégorie qui correspond le mieux à votre demande pour nous permettre de vous orienter vers les solutions les plus adaptées.',
                id: 'motif_contact',
                type: 'radio',
                options: [
                    {
                        value: 'sinistre',
                        label: "J'ai un sinistre",
                        icon: sinistre,
                    },
                    ...(hasApporteur43397
                        ? [
                              {
                                  value: 'demande_devis',
                                  label: 'Je réalise un devis / souscris un contrat',
                                  icon: contract,
                              },
                          ]
                        : []),
                    {
                        value: 'gestion_contrat',
                        label: 'Je gère mon contrat',
                        icon: contract_sign,
                    },
                    ...(hasApporteur43397
                        ? [
                              {
                                  value: 'resiliation',
                                  label: 'Je résilie mon contrat',
                                  icon: resiliation,
                              },
                          ]
                        : []),
                    {
                        value: 'reclamation',
                        label: 'Je souhaite faire une réclamation',
                        icon: reclamation,
                    },
                    {
                        value: 'autre',
                        label: "J'ai une autre demande",
                        icon: conversation,
                    },
                ],
            },
            {
                id: 'proc_devis_deja',
                type: 'radio',
                label: 'Que souhaitez-vous <span>faire ?</span>',
                info: 'Choisissez la catégorie qui correspond le mieux à votre demande pour nous permettre de vous orienter vers les solutions les plus adaptées.',
                depends_on: [
                    {
                        field: 'motif_contact',
                        values: ['demande_devis', 'sinistre', 'gestion_contrat', 'resiliation', 'reclamation', 'autre'],
                    },
                ],
                options: {
                    demande_devis: [
                        {
                            value: 'vehicule_supplementaire',
                            label: 'Assurer un véhicule supplémentaire',
                            icon: vehicule_supp,
                        },
                        {
                            value: 'changer_vehicule',
                            label: 'Changer de véhicule assuré',
                            icon: change_vehicule,
                        },
                    ],
                    sinistre: [
                        {
                            value: 'declarer_sinistre',
                            label: 'Déclarer un sinistre',
                            icon: dec_sinistre,
                        },
                        {
                            value: 'suivi_sinistre',
                            label: 'Suivre un sinistre en cours',
                            icon: sinistre_encours,
                        },
                        {
                            value: 'envoyer_documents',
                            label: 'Envoyer des documents',
                            redirect: '/mes-sinistres',
                            icon: send_docs,
                        },
                    ],
                    gestion_contrat: [
                        {
                            value: 'maj_contrat',
                            label: 'Modifier mon contrat',
                            icon: update_contract,
                        },
                        {
                            value: 'informations_contrat',
                            label: 'Avoir des informations sur mon contrat',
                            icon: info_contract,
                        },
                        {
                            value: 'download_documents',
                            label: 'Télécharger mes documents',
                            icon: send_docs,
                        },
                        {
                            value: 'show_paiements',
                            label: 'Consulter mes paiements',
                            icon: paiements,
                        },
                    ],
                    resiliation: [
                        {
                            value: 'resiliation_redirect',
                            label: 'Résilier mon contrat en ligne',
                            redirect: '/resilier',
                            icon: resiliation,
                        },
                        ...faqOption('/mes-questions?contact=gestion#resiliation'),
                    ],
                    reclamation: [...envoyerMessageOption],
                    autre: [...(hasContractApporteur43397 ? envoyerMessageOption : []), ...faqOption('/mes-questions?contact=gestion#autres_questions')],
                },
            },
            {
                id: 'etape_3_proc_devis_deja_declarer_sinistre',
                type: 'radio',
                info: '',
                label: "Disposez-vous d'un <span>constat amiable ?</span>",
                depends_on: [
                    {
                        field: 'proc_devis_deja',
                        values: ['declarer_sinistre'],
                    },
                ],
                options: {
                    declarer_sinistre: [
                        {
                            value: 'constat_oui',
                            label: "Oui, j'ai un constat",
                            icon: constatYes,
                        },
                        {
                            value: 'constat_non',
                            label: "Non, je n'ai pas de constat",
                            icon: constatNo,
                        },
                    ],
                },
            },
            {
                id: 'etape_3_proc_devis_deja',
                type: 'radio',
                label: 'Comment souhaitez-vous <span>procéder ?</span>',
                depends_on: [
                    {
                        field: 'proc_devis_deja',
                        values: [
                            'suivi_sinistre',
                            'vehicule_supplementaire',
                            'changer_vehicule',
                            'maj_contrat',
                            'informations_contrat',
                            'download_documents',
                            'show_paiements',
                        ],
                    },
                ],
                options: {
                    suivi_sinistre: [
                        {
                            value: 'declarer_sinistre',
                            label: "Suivre l'avancée de mon sinistre en ligne",
                            icon: dec_sinistre,
                            redirect: '/mes-sinistres',
                        },
                        {
                            value: 'appeler',
                            label: `Appeler ${brandName}`,
                            html: "<a href='tel:0247510707'>02 47 51 07 07</a>",
                            icon: phoneCall,
                            link: 'tel:0247510707',
                        },
                        ...faqOption('/mes-questions/?contact=gestion#sinistre'),
                    ],
                    vehicule_supplementaire: [
                        {
                            value: 'appeler',
                            label: `Appeler ${brandName}`,
                            html: "<a href='tel:0255453458'>02 55 45 34 58</a>",
                            icon: phoneCall,
                            link: 'tel:0255453458',
                        },
                        {
                            value: 'connect_account_devis',
                            label: 'Réaliser un devis en ligne',
                            redirect: subscribeLinks['moto'].split('?')[0],
                            icon: espace_assure,
                        },
                    ],
                    changer_vehicule: [
                        {
                            value: 'account_avenant',
                            label: 'Faire un avenant de changement de véhicule',
                            onClickAction: 'avenant',
                            icon: espace_assure,
                        },
                        ...envoyerMessageOption,
                    ],
                    maj_contrat: [
                        ...(hasApporteur43397
                            ? [
                                  {
                                      value: 'account_avenant',
                                      label: 'Faire un avenant',
                                      onClickAction: 'avenant',
                                      icon: espace_assure,
                                  },
                              ]
                            : []),
                        ...faqOption('/mes-questions?contact=gestion#contrat'),
                    ],
                    informations_contrat: [
                        {
                            value: 'consult_contract',
                            label: 'Consulter mon contrat',
                            redirect: '/mes-contrats',
                            icon: contract_sign,
                        },
                        ...faqOption('/mes-questions?contact=gestion#contrat'),
                    ],
                    download_documents: [
                        {
                            value: 'redirect_download_documents',
                            label: 'Télécharger mes documents',
                            redirect: '/mes-documents',
                            icon: contract,
                        },
                        ...faqOption('/mes-questions?contact=gestion#contrat'),
                    ],
                    show_paiements: [
                        {
                            value: 'consult_paiements',
                            label: 'Consulter mes paiements',
                            redirect: '/mes-paiements',
                            icon: paiements,
                        },
                        ...faqOption('/mes-questions?contact=gestion#contrat'),
                    ],
                },
            },
            {
                id: 'etape_4_proc_devis_deja_declarer_sinistre',
                type: 'radio',
                info: '',
                label: 'Comment souhaitez-vous <span>procéder ?</span>',
                depends_on: [
                    {
                        field: 'etape_3_proc_devis_deja_declarer_sinistre',
                        values: ['constat_oui'],
                    },
                ],
                options: {
                    constat_oui: [
                        {
                            value: 'declarer_sinistre',
                            label: 'Déclarer mon sinistre en ligne',
                            icon: dec_sinistre,
                            redirect: '/mes-sinistres',
                        },
                        {
                            value: 'appeler',
                            label: `Appeler ${brandName}`,
                            html: "<a href='tel:0247510707'>02 47 51 07 07</a>",
                            icon: phoneCall,
                            link: 'tel:0247510707',
                        },
                        ...faqOption('/mes-questions/?contact=gestion#sinistre'),
                    ],
                },
            },
            {
                id: 'etape_4_proc_devis_deja_declarer_sinistre_non',
                type: 'radio',
                info: 'Sans constat amiable, aucune déclaration de sinistre ne peut être enregistrée par téléphone. <br/> Déclarez votre sinistre directement en ligne, 24h/24, 7j/7, depuis votre espace assuré.',
                label: 'Comment souhaitez-vous <span>procéder ?</span>',
                depends_on: [
                    {
                        field: 'etape_3_proc_devis_deja_declarer_sinistre',
                        values: ['constat_non'],
                    },
                ],
                options: {
                    constat_non: [
                        {
                            value: 'declarer_sinistre',
                            label: 'Déclarer mon sinistre en ligne',
                            icon: dec_sinistre,
                            redirect: '/mes-sinistres',
                        },
                        ...faqOption('/mes-questions/?contact=gestion#sinistre'),
                    ],
                },
            },
            {
                id: 'formulaire_message',
                type: 'group',
                custom_visibility: 'or',
                depends_on: [
                    {
                        field: 'proc_devis',
                        values: ['envoyer_message'],
                    },
                    {
                        field: 'proc_devis_deja',
                        values: ['envoyer_message'],
                    },
                    {
                        field: 'etape_3_proc_devis_deja',
                        values: ['envoyer_message'],
                    },
                ],
            },
        ],
    };

    const handleMultipleContracts = (action: ContractAction) => {
        if (!contracts || contracts.length === 0) return;

        if (contracts.length === 1) {
            executeContractAction(contracts[0], action);
            return;
        }

        setPickerAction(action);
        setPickerOpen(true);
    };

    const executeContractAction = (contract: Contract, action: ContractAction | null) => {
        switch (action) {
            case 'avenant':
                window.open(contract.avenant_link, '_blank');
                break;
        }
    };

    if (typeof window !== 'undefined') {
        window.handleMultipleContracts = handleMultipleContracts;
    }

    const scrollToStep = (stepId: string, block: ScrollLogicalPosition = 'center') => {
        const el = refs.current[stepId];
        if (el && el.scrollIntoView) {
            el.scrollIntoView({ behavior: 'smooth', block });
        }
    };

    const handleClick = (stepId: string, value: string, redirect?: string, onClickAction?: ContractAction) => {
        const clearedState = resetDependentFields(stepId);
        const newState = { ...clearedState, [stepId]: value };
        setFormState(newState);

        const navigationLevel = getStepLabel(stepId);
        const buttonLabel = getOptionLabel(stepId, value, newState);
        const parentLabels = getParentLabels(stepId, newState);

        pushEvent('contactFormButtonClick', {
            'contactFormButtonClick': {
                'navigationLevel': navigationLevel,
                'label': buttonLabel,
                'parentLabel': parentLabels
            }
        });

        if (redirect) {
            setTimeout(() => {
                window.open(redirect, '_blank');
            }, 100);
            return;
        }

        if (onClickAction) {
            handleMultipleContracts(onClickAction);
            return;
        }

        const nextStep = formStructure.etapes.find(
            (s) =>
                s.id !== stepId &&
                isStepVisible(s, newState) &&
                !formState[s.id]
        );

        if (nextStep) {
            setTimeout(() => scrollToStep(nextStep.id), 100);
        }
    };

    const isStepVisible = (step: ContactStep, state: ContactFormState = formState) => {
        if (!step.depends_on) return true;

        const visibilityMode = step.custom_visibility === 'or' ? 'or' : 'and';

        const result = visibilityMode === 'or'
            ? step.depends_on.some(dep => state[dep.field] && dep.values.includes(state[dep.field]!))
            : step.depends_on.every(dep => state[dep.field] && dep.values.includes(state[dep.field]!));

        return result;
    };

    const resolveStepOptions = (step: ContactStep, state: ContactFormState = formState): ContactOption[] => {
        if (!step.options) return [];

        if (Array.isArray(step.options)) {
            return step.options;
        }

        // Cherche dans les conditions de dépendance une clé valide dans les options
        for (const dep of step.depends_on || []) {
            const val = state[dep.field];
            if (val && step.options[val]) {
                return step.options[val]!;
            }
        }

        return [];
    };

    const resetDependentFields = (changedFieldId: string, currentState: ContactFormState = formState) => {
        const updatedState = { ...currentState };

        const purgeCascade = (fieldId: string) => {
            formStructure.etapes.forEach(step => {
                if (step.depends_on?.some(dep => dep.field === fieldId)) {
                    if (Object.prototype.hasOwnProperty.call(updatedState, step.id)) {
                        delete updatedState[step.id];
                        purgeCascade(step.id); // Purge récursivement
                    }
                }
            });
        };

        purgeCascade(changedFieldId);
        return updatedState;
    };

    /*
     * Présélection des étapes du parcours via l'URL : un paramètre par étape,
     * nommé comme l'étape ('motif_contact', 'proc_devis_deja', 'etape_3_proc_devis_deja', ...)
     * et valorisé comme l'option à cocher. Les étapes sont appliquées dans l'ordre et seules
     * les options réellement proposées au client sont retenues.
     * Ex : /contact?motif_contact=autre&proc_devis_deja=envoyer_message
     * ouvre directement "J'ai une autre demande > Envoyer un message".
     */
    const getStateFromSearchParams = () => {
        const state: ContactFormState = {};

        formStructure.etapes.forEach(step => {
            const value = searchParams.get(step.id);

            if (!value || !isStepVisible(step, state)) return;

            if (resolveStepOptions(step, state).some(opt => opt.value === value)) {
                state[step.id] = value;
            }
        });

        return state;
    };

    useEffect(() => {
        if (deepLinkApplied.current) return;

        const deepLinkState = getStateFromSearchParams();

        if (Object.keys(deepLinkState).length === 0) return;

        deepLinkApplied.current = true;
        setFormState(deepLinkState);

        const lastStep = [...formStructure.etapes].reverse().find(step => isStepVisible(step, deepLinkState));

        if (lastStep) {
            /* "start" plutôt que "center" : la carte du formulaire est trop haute pour être centrée */
            setTimeout(() => scrollToStep(lastStep.id, 'start'), 300);
        }
    }, [searchParams]);

    return (
        <>
        <div className="mt-6 space-y-6">
            {formStructure.etapes.map((step) => {
                if (!isStepVisible(step)) return null;

                const options = resolveStepOptions(step);

                return (
                    <Card
                        key={step.id}
                        ref={(el) => { refs.current[step.id] = el; }}
                    >
                        <CardHeader className="pb-6 border-b">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <HelpCircle className="w-5 h-5 text-primary" aria-hidden="true" />
                                <span id={`${step.id}-label`} className="[&_span]:text-primary" dangerouslySetInnerHTML={{__html: step.label as string}} />
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6">
                            {step.info && (
                                <p
                                    className={`text-sm mb-6 ${
                                        step.id === "etape_4_proc_devis_deja_declarer_sinistre_non"
                                            ? "text-primary font-semibold bg-primary/10 p-4 border-l-4 border-primary rounded-r-md"
                                            : "text-muted-foreground"
                                    }`}
                                    dangerouslySetInnerHTML={{__html: step.info}}
                                />
                            )}

                            {step.type === 'radio' && (
                                <div role="radiogroup" aria-labelledby={`${step.id}-label`} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                    {options.map(opt => {
                                        const isSelected = formState[step.id] === opt.value;
                                        const cardClass = `flex flex-col items-center justify-center text-center p-6 h-[130px] rounded-lg border cursor-pointer font-semibold text-xs transition-all duration-200 no-underline
 ${isSelected
                                                ? 'border-primary bg-primary/5 shadow-md'
                                                : 'border-[#818181] bg-muted/30 hover:border-primary hover:bg-primary/5 shadow-sm'
                                            }
                                            peer-focus-visible:outline peer-focus-visible:outline-3 peer-focus-visible:outline-accent peer-focus-visible:outline-offset-2
                                        `;
                                        const optionInner = renderOptionInner(opt);

                                        if (opt.redirect && !opt.onClickAction) {
                                            const isExternal = /^https?:\/\//.test(opt.redirect);
                                            return isExternal ? (
                                                <a key={opt.value} href={opt.redirect} target="_blank" rel="noreferrer noopener" className={cardClass}>
                                                    {optionInner}
                                                </a>
                                            ) : (
                                                <Link key={opt.value} to={opt.redirect} className={cardClass}>
                                                    {optionInner}
                                                </Link>
                                            );
                                        }

                                        return (
                                            <div key={opt.value}>
                                                <input
                                                    className="peer sr-only"
                                                    type="radio"
                                                    id={`${step.id}_${opt.value}`}
                                                    name={step.id}
                                                    value={opt.value}
                                                    checked={isSelected}
                                                    onChange={() => handleClick(step.id, opt.value, opt.redirect, opt.onClickAction)}
                                                />
                                                <label htmlFor={`${step.id}_${opt.value}`} className={cardClass}>
                                                    {optionInner}
                                                </label>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {step.type === 'group' && (
                                <FormulaireContact
                                    formState={formState}
                                    getOptionLabel={getOptionLabel}
                                />
                            )}
                        </CardContent>
                    </Card>
                );
            })}
        </div>
        <ContractPickerModal
            isOpen={pickerOpen}
            onClose={() => setPickerOpen(false)}
            onSelect={(c) => executeContractAction(c, pickerAction)}
            contracts={contracts}
        />
        </>
    );
};

export default ContactezNous;
